import React, { useEffect, useState, useCallback } from 'react';
import './HomePage.scss';
import Table from 'componentsV2/Table/Table';
import { CorePagination } from '@komodo-design-system/komodo-components-react';
import { useDispatch, useSelector } from 'react-redux';
import { searchActions } from 'actions/search';
import { advancedSearchPayloadType, simpleSearchPayloadType } from 'type/homeType';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import Loading from 'components/Placeholders/Loading';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import ActionPopUp from './ActionPopUp';
import { BASE_URL } from 'constants/routes';
import SKUTable from './SKUTable';
import Actions from 'componentsV2/Shared/Actions';
import AlertBox from 'componentsV2/SharedAlertBox/AlertBox/AlertBox';
import { REFRESH_BUTTON_TITLE } from 'constants/constants';
import { dateFormatter } from 'utils/utils';

const HomePage = () => {
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const navigate = useNavigate();

  // Selectors
  const searchKeyword = useSelector((state: any) => state.getSearchValues.keyword);
  const {userName} = useSelector((state: any) =>  state?.userTypeReducer?.userType?.data || { userName: '' });
  const recentData = useSelector((state: any) => state.getAdvancedSearch.data);
  const recentDataLoading = useSelector((state: any) => state.getAdvancedSearch.loading);
  const searchData = useSelector((state: any) => state.getSimpleSearch.data);
  const searchDataLoading = useSelector((state: any) => state.getSimpleSearch.loading);
  const paginationValues = useSelector((state: any) => state.getSearchValues.pagination);
  const loading = useSelector((state: any) => state.loading.isLoading);
  // States
  const [alert, setAlert] = useState({open: false, row: {} });
  const [filteredData, setFilteredData] = useState<any>({ records: [], totalRecords: [], count: 0 });

  const pageNo = paginationValues?.pageNumber || 1;
  const rpp = paginationValues?.pageSize || 10;
  const isSearch = searchKeyword?.length > 0;
  const [actionPopUp, setActionPopUp] = useState({
    isOpen: false,
    inputProps: {},
    iconRef: "" 
  });
  const [actions, setActions] = useState<{
    operate: boolean;
    action: string;
    inputProps: any;
    handleActionResponse: (response: any) => void;
      }>({
        operate: false,
        action: '',
        inputProps: {},
        handleActionResponse: () => {}
      });
  const [responseAlert, setResponseAlert] = useState({ isOpen: false, message: '' });

  const getFileName = (filePath: string) => {
    if(!filePath) {
      return '';
    }
    const fileName = filePath.split('/').pop()?.split('_').slice(0, -1).join('_') || '';
    const extension = filePath.split('.').pop();
    return `${fileName}${extension ? `.${extension}` : ''}`;
  };

  // Utility function to filter data
  const filterData = (data: any) =>
    data?.asset_metadata_search_results?.map((record: any) => ({
      ...record,
      typeIcon: getAssetType(record['ARTESIA.FIELD.MIME TYPE']?.split('/')[1] || ''),
    }));

  // Fetch recent uploads
  const fetchRecentUploads = useCallback(
    async (pageNumber: number, pageSize: number) => {
      const payload: advancedSearchPayloadType = { userId: userName, pageNumber, pageSize };
      dispatch(searchActions.advancedSearch(payload));
    },
    [dispatch, userName]
  );

  // Fetch simple search results
  const fetchSimpleSearch = useCallback(
    async (keyword: string, pageNumber: number, pageSize: number) => {
      const payload: simpleSearchPayloadType = { keyword, pageNumber, pageSize };
      dispatch(searchActions.simpleSearch(payload));
    },
    [dispatch]
  );

  // Handle data updates
  useEffect(() => {
    setAlert({ open: false, row: {} });
    const updateFilteredData = (data: any) => {
      const filtered = filterData(data);
      setFilteredData({
        records: filtered,
        totalRecords: data?.asset_metadata_search_results,
        count: data?.total,
      });
    };

    if (isSearch && searchData && !searchDataLoading) {
      updateFilteredData(searchData);
    } else if (recentData && !recentDataLoading) {
      updateFilteredData(recentData);
    }
  }, [recentData, searchData, isSearch, searchDataLoading, recentDataLoading]);



  useEffect(() => {
    window.scrollTo(0, 0);
    if (isSearch) {
      fetchSimpleSearch(searchKeyword, pageNo, rpp);
    } else {
      fetchRecentUploads(pageNo, rpp);
    }
  }, [rpp, pageNo]);

  const handleRefreshClick = () => {
    window.scrollTo(0, 0);
    if (isSearch) {
      fetchSimpleSearch(searchKeyword, pageNo, rpp);
    } else {
      fetchRecentUploads(pageNo, rpp);
    }
  };


  // Utility function to get asset type icon
  const getAssetType = (assetType: string) => {
    const imageType = ['jpg', 'jpeg', 'png', 'eps', 'tiff/tif', 'gif', 'bmp', 'psd', 'svg', 'zip', 'glb'];
    const icons: { [key: string]: JSX.Element } = {
      // png: <span className="icon-24-image-bold"></span>,
      mp3: <span className="icon-24-video-bold"></span>,
      pdf: <span className="icon-24-docs-pdf-bold"></span>,
    };
      // Check if the assetType is an image type
    if (imageType.includes(assetType.toLowerCase())) {
      return <span className="icon-24-image-bold"></span>;
    }

    return icons[assetType] || <span className="icon-24-document-bold"></span>;
  };

  // Handlers
  const handleResultsPerPageChange = (pageSize: number) => {
    window.scrollTo(0, 0);
    dispatch(searchActions.getSearchValues(searchKeyword, { pageNumber: 1, pageSize }));
  };

  const handlePagination = (pageNumber: number) => {
    window.scrollTo(0, 0);
    dispatch(searchActions.getSearchValues(searchKeyword, { pageNumber, pageSize: rpp }));
  };

  const handleSKUInfo = (row: any) => {
    setAlert({
      open: true,
      row: row.product_identifiers || [],
    });
  };

  const handleClose = () => {
    setAlert({ ...alert, open: false, row: {} });
  };


  const handleAssetDetail = (type: any, accessor: any, row: any) => {
    navigate(`${BASE_URL}/asset-detail/${row?.asset_id}`);
    window.scrollTo(0, 0);
  };
  const handleActionResponse = (response: any,action:string,inputProps:any) => {
    setActions({ operate: false, action: '', inputProps: {}, handleActionResponse: () => {} }); // Reset actions state
    if (response?.status?.toString() === '200') {
      setResponseAlert({
        isOpen: true,
        message: `Asset ${action} successfully.`,
      });
    } else {
      setResponseAlert({
        isOpen: true,
        message: `Failed to ${action} asset.`,
      });
    }
  };
  const handleAction = (action: string, inputProps?: any) => {
    setActionPopUp({isOpen: false, inputProps: {},iconRef:''}); // Close the popup after action
    setActions({
      operate: true,
      action,
      inputProps,
      handleActionResponse: (response) => handleActionResponse(response, action, inputProps)
    });
  };

  const handleCloseAlert = () => {
    setResponseAlert({ isOpen: false, message: '' });
    // Refresh the data after action
    if (isSearch) {
      fetchSimpleSearch(searchKeyword, pageNo, rpp);
    } else {
      fetchRecentUploads(pageNo, rpp);
    }
  };
  // Table columns
  const columns = [
    {
      header: 'Asset Type', accessor: 'assetType', sortable: true,
      onCellClick: (type: any, accessor: any, row: any) => handleAssetDetail(type, accessor, row),
      cell: (row: any) => <span className='asset-type'>{row.model_id}</span>
    },
    {
      header: 'Title',
      accessor: 'title',
      sortable: true,
      cell: (row: any) => (
        <span
          className="title-cell"
          title={row.title || row.article_title} // Tooltip with full title
        >
          {row.title || row.article_title}
        </span>
      )
    },
    { header: 'Type', accessor: 'typeIcon', sortable: true, cell: (row: any) => <span>{row.typeIcon}</span> },
    {
      header: 'Name', accessor: 'name', sortable: true,
      // onCellClick: () =>  {},
      cell: (row: any) =>(
        <span
          className="title-cell"
          title={row['ARTESIA.FIELD.ASSET NAME']} // Tooltip with full path
        >
          {row['ARTESIA.FIELD.ASSET NAME']}
        </span>
      )
    },
    {
      header: 'SKU',
      accessor: 'sku',
      sortable: true,
      cell: (row: any) => {
        const sku = row?.product_identifiers?.map((item: any) => item.webId);
        return sku?.length > 0 ? (
          <span className="view-more" title="View More" onClick={() => handleSKUInfo(row)}>
            {sku?.length === 1 ? sku[0] : 'Multiple SKUs'}
          </span>
        ) : <span></span>;
      }
    },
    {
      header: 'Published Date', accessor: 'publishedDate', sortable: true, cell: (row: any) =>
        <span>{row?.publishedDate
          ? dateFormatter(row?.publishedDate)
          : ''}</span>
    },
    { header: 'Status', accessor: 'status', sortable: true, cell: (row: any) => <span>{row.file_status || 'New'}</span> },
  ];

  const handleActionIcon = (action:string,row:any,index:number) => {
    setActionPopUp({
      isOpen: true,
      inputProps: row || {},
      iconRef: `action-icon-${action}-${index}`,
    });

  };



  const getActionIcons = () => [
    {
      icon: 'icon-16-moreVert-bold',
      name: 'actions',
      tooltip: 'Actions',
      onClick: (action:string,row:any,index:number) => handleActionIcon(action,row, index),
    },
  ];

  return (
    <>
      {isSearch ? searchDataLoading && <Loading /> : recentDataLoading && <Loading />}
      <div className="home-page" data-testid="home-page">
        <div className="page-title">{isSearch ? 'Search Results' : 'My Recent Uploads'}
          <span
            className="icon-24-refresh-bold refresh-icon"
            onClick={handleRefreshClick}
            title={REFRESH_BUTTON_TITLE}></span>{' '}
        </div>
        <Table data={filteredData?.records || []} columns={columns} 
          actionIcons= {getActionIcons()}
        />
        {filteredData?.count > 10 && (
          <CorePagination
            style={{ position: 'relative', bottom: '10px' }}
            variants={{ dropdown: true, indicator: false, goto: false }}
            resultsperpage={[10, 20, 30]}
            totalresults={filteredData?.count || 0}
            activepage={pageNo}
            activeresultsperpage={rpp}
            onResultsPerPageChange={(event) => handleResultsPerPageChange(event.detail)}
            onActivePageChange={(event) => handlePagination(event.detail.activePage)}
          />
        )}
      </div>
      {alert?.open && <div className='sku-region-modal'>
        <SKUTable
          alert={alert} // Modal open state
          handleClose={handleClose} // Close the alert
        />
      </div>
      }
      {actionPopUp?.isOpen && <ActionPopUp action={actionPopUp} handleAction={handleAction}  />}
      {actions.operate && <Actions action={actions?.action} inputProps={actions?.inputProps} handleActionResponse={actions?.handleActionResponse} />}
      {responseAlert?.isOpen && <AlertBox isModalOpen message={responseAlert?.message} handleClose={handleCloseAlert} />}
      {loading && <Loading />}
    </>
  );
};

export default HomePage;
